import React, { useCallback, useMemo } from 'react';

import {
  Box,
  Checkbox,
  FormikFormControl,
  FormikInput,
  HStack,
  Text,
} from '@rbilabs/universal-components';
import { Formik } from 'formik';
import { useIntl } from 'react-intl';
import { NativeSyntheticEvent, TextInputChangeEventData } from 'react-native';

import ActionButton from 'components/action-button';
import Link from 'components/link';
import { IsoCountryCode } from 'generated/graphql-gateway';
import { useLoyaltyContent } from 'hooks/loyalty/use-loyalty-content';
import { useFieldIsRequired } from 'hooks/use-field-is-required';
import { ClickEventComponentNames, logButtonClick } from 'state/amplitude';
import { UserDetails } from 'state/auth/hooks/use-current-user';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { enableNumericPhoneValidation } from 'utils/environment';
import {
  POSSIBLE_DEFAULT_OR_EMPTY_DOB_VALUES,
  formatDateObject as dobObjToString,
  dobStringToObj,
  sanitizePhoneNumber,
} from 'utils/form';
import { postalCodeInputProperties } from 'utils/form/constants';
import { IDateOfBirth } from 'utils/form/types';
import { validationConfig } from 'utils/form/user-profile-validation-config/validation';
import { routes } from 'utils/routing';

import { FormikBirthdayInput } from '../formik/birthday-input';
import CompletePhoneFromBrazeId from '../phone-from-braze-id/complete-phone-from-braze-id';

import { getFormErrors } from './get-form-errors';
import { TermsSection } from './terms-section';
import { IUserFormData, IUserInfoSubmitData } from './types';

const InputContainer = Box.withConfig({
  marginBottom: '$3',
});

interface IUserInfoFormProps {
  readOnly?: boolean;
  user: UserDetails | null;
  /**
   * TODO: this onSubmit requires that a promise be passed
   * because there's a `.then` chain after it's called.
   *
   * We should either move the after submit functionality up to the account-info page
   * or move the submit logic into this form.
   */
  onSubmit: (data: IUserInfoSubmitData) => Promise<any>;
  isLoading?: boolean;
}

export const UserInfoForm = ({
  onSubmit,
  readOnly = false,
  user,
  isLoading,
}: IUserInfoFormProps) => {
  const enableAdsPersonalizationOptOut = useFlag(
    LaunchDarklyFlag.ENABLE_ADS_PERSONALIZATION_OPT_OUT
  );
  const { formatMessage } = useIntl();
  const { region } = useLocale();
  const { birthdayLegalText } = useLoyaltyContent();

  const DOB_DEFAULT_OR_EMPTY = useMemo(
    () => POSSIBLE_DEFAULT_OR_EMPTY_DOB_VALUES.some(v => v === user?.details?.dob || ''),
    [user]
  );

  const initialValues: IUserFormData = useMemo(() => {
    // dob will come in as string so let's convert it
    const dob = dobStringToObj(user?.details?.dob);
    return {
      agreesToTermsOfService: true,
      dob,
      email: user?.details?.email || '',
      name: user?.details?.name || '',
      phoneNumber: user?.details?.phoneNumber || '',
      promotionalEmails: !!user?.details?.promotionalEmails || false,
      isoCountryCode: (user?.details?.isoCountryCode as IsoCountryCode) || IsoCountryCode.USA,
      zipcode: user?.details?.zipcode || '',
      defaultReloadAmt: user?.details?.defaultReloadAmt || 500,
      defaultReloadPaymentMethodId: user?.details?.defaultPaymentAccountId || '',
      defaultCheckoutPaymentMethodId:
        user?.details?.defaultAccountIdentifier || user?.details?.defaultFdAccountId || '',
      autoReloadEnabled: user?.details?.autoReloadEnabled || false,
      autoReloadThreshold: user?.details?.autoReloadThreshold || 500,
      adsPersonalizationOptOut: user?.details?.adsPersonalizationOptOut ?? false,
    };
  }, [user]);

  const handleSubmit = useCallback(
    async (formData: IUserFormData) => {
      const dob = dobObjToString(formData.dob as IDateOfBirth);

      const phoneNumber = formData.phoneNumber
        ? await sanitizePhoneNumber(formData.phoneNumber, formData.isoCountryCode)
        : null;
      await onSubmit({
        ...formData,
        phoneNumber,
        dob,
      });
    },
    [onSubmit]
  );

  const sendPersonalizationOptOutClickEvent = (checked: boolean) => {
    logButtonClick({
      attributes: {
        component: ClickEventComponentNames.BUTTON,
        name: 'internet-based advertising checkbox',
        checked,
      },
    });
  };

  const birthdayIsRequired = useFieldIsRequired(region, validationConfig, 'dob');
  const zipcodeIsRequired = useFieldIsRequired(region, validationConfig, 'zipcode');
  const phoneNumberIsRequired = useFieldIsRequired(region, validationConfig, 'phoneNumber');

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={getFormErrors({
        formatMessage,
        region,
        simplePhoneValidation: enableNumericPhoneValidation(),
      })}
      enableReinitialize
    >
      {({
        handleChange,
        handleBlur,
        submitForm,
        values,
        setFieldValue,
        isValid,
        isSubmitting,
        setFieldTouched,
        errors,
        touched,
      }) => (
        <>
          <CompletePhoneFromBrazeId setFieldValue={setFieldValue} />
          <InputContainer>
            <FormikFormControl
              name="email"
              label={formatMessage({ id: 'yourEmail' })}
              isReadOnly
              testID="email"
            >
              <FormikInput
                name="email"
                inputProps={{
                  isDisabled: true,
                  isRequired: true,
                  testID: 'account-info-email-input',
                }}
              />
            </FormikFormControl>
            <FormikFormControl name="name" label={formatMessage({ id: 'name' })} isRequired>
              <FormikInput
                name="name"
                inputProps={{
                  isRequired: true,
                  isDisabled: readOnly,
                  testID: 'account-info-name-input',
                }}
              />
            </FormikFormControl>
            <FormikFormControl
              isRequired={phoneNumberIsRequired}
              name="phoneNumber"
              label={formatMessage({ id: 'phoneNumber' })}
            >
              <FormikInput
                name="phoneNumber"
                inputProps={{
                  placeholder: `${formatMessage({ id: 'phoneNumber' })}`,
                  testID: 'signup-phone-input',
                  autoComplete: 'tel',
                  isDisabled: readOnly,
                  keyboardType: 'phone-pad',
                }}
              />
            </FormikFormControl>
            <FormikFormControl name="dob">
              <FormikBirthdayInput
                disabled={readOnly || !DOB_DEFAULT_OR_EMPTY}
                hintMessage={birthdayLegalText ?? ''}
                isRequired={birthdayIsRequired}
                year={values.dob.year}
                month={values.dob.month}
                day={values.dob.day}
                onChange={(inputName, inputText) => setFieldValue(`dob.${inputName}`, inputText)}
                testID="account-info-dob"
                onBlur={inputName => setFieldTouched(`dob.${inputName}`, true)}
                dobErrors={errors.dob}
                dobTouched={touched.dob}
              />
            </FormikFormControl>
            <FormikFormControl
              name="zipcode"
              isRequired={zipcodeIsRequired}
              label={formatMessage({
                id: values.isoCountryCode === IsoCountryCode.USA ? 'zipCode' : 'postalCode',
              })}
            >
              <FormikInput
                name="zipcode"
                inputProps={{
                  placeholder: formatMessage({
                    id: values.isoCountryCode === IsoCountryCode.USA ? 'zipCode' : 'postalCode',
                  }),
                  isDisabled: readOnly,
                  testID: 'account-info-zip-input',
                  onBlur: () => handleBlur('zipcode'),
                  onChange: (event: NativeSyntheticEvent<TextInputChangeEventData>) => {
                    setFieldValue('zipcode', event.target.toString().toUpperCase());
                  },
                  maxLength: postalCodeInputProperties[values.isoCountryCode]?.maxLength || 10,
                  autoComplete: 'postal-code',
                  ...(values.isoCountryCode === IsoCountryCode.USA && {
                    keyboardType: 'phone-pad',
                  }),
                }}
              />
            </FormikFormControl>
            {enableAdsPersonalizationOptOut && (
              <>
                <HStack alignItems="center" mt="$2">
                  <Checkbox
                    value={'ads-personalization-opt-out'}
                    aria-label={formatMessage({ id: 'adsPersonalization' })}
                    isChecked={!values.adsPersonalizationOptOut}
                    onChange={checked => {
                      sendPersonalizationOptOutClickEvent(checked);
                      setFieldValue('adsPersonalizationOptOut', !checked);
                    }}
                  />
                  <Text ml="$3" flex="1">
                    {formatMessage({ id: 'adsPersonalization' })}
                  </Text>
                </HStack>
                <Text my="$2">
                  {formatMessage(
                    { id: 'adsPersonalizationInfo' },
                    {
                      privacyPolicyLink: (
                        <Link isUnderlined to={routes.privacyPolicy}>
                          {formatMessage({ id: 'privacyPolicy' })}
                        </Link>
                      ),
                    }
                  )}
                </Text>
              </>
            )}
          </InputContainer>
          <Box>
            <ActionButton
              testID="account-info-form-submit-button"
              fullWidth
              onPress={submitForm}
              disabled={readOnly || !isValid || isLoading || isSubmitting}
              isLoading={isLoading || isSubmitting}
            >
              {formatMessage({ id: 'update' })}
            </ActionButton>
          </Box>
          <TermsSection />
        </>
      )}
    </Formik>
  );
};
